import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  NavIcon,
  PageHeader,
  orgLogoUrl,
  orgLogoHorizontalUrl,
  useBrandTokens,
  type ShellOutletContext,
} from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qPrescriptionPrintSettings,
  mapUIToPrescriptionPrintSettingsColumns,
  PRESCRIPTION_PRINT_DEFAULTS,
  qAppConfig,
  qUserProviderLink,
  qAppointmentProviders,
  getPrintBranding,
  writePrintBranding,
  letterheadFromBranding,
  normalizePrintFontColor,
  type PrintBrandingConfig,
  type PrintLetterheadInfo,
  type PrescriptionPrintSettingsRow,
  type EncounterPrintClinical,
  type PrescriptionPrintSettingsUI,
  type PrescriptionPrintTemplateId,
  type PrescriptionPrintLogoSource,
  type PrescriptionPrintClinicalLayoutSettings,
  type PrescriptionPrintTypographySettings,
  LOGO_SIZES,
  HEADER_SIZES,
  VITALS_LAYOUTS,
  FONT_SCALES,
  FONT_SCALE_CHOICES,
  MULTI_VALUE_LAYOUTS,
  type AppConfigRow,
  type UserProviderLinkRow,
  type AppointmentProviderRow,
} from '@helios/data-access';
import { SYSTEM_TEMPLATES, templateSupports, type SystemTemplate } from './system-templates';
import { PrescriptionPrintLayout, type PrintPrescriptionItem } from './PrescriptionPrintLayout';
import { printHtmlViaIframe, resolveDoctor, resolvePrintSettings } from './print-utils';

// ─── Steps ───

const STEPS = [
  { num: 1, label: 'Template' },
  { num: 2, label: 'Branding' },
  { num: 3, label: 'Sections' },
  { num: 4, label: 'Preview & Save' },
] as const;

type StepNum = (typeof STEPS)[number]['num'];

// ─── Sample data for preview (PRD §6.4) ───

const SAMPLE_ITEMS: PrintPrescriptionItem[] = [
  {
    itemName: 'Paracetamol 500 mg',
    itemDetails: 'Crocin Advance, tablet',
    intakeTiming: 'after_food',
    doseQuantity: '1 tab',
    scheduleMorning: true,
    scheduleAfternoon: true,
    scheduleNight: true,
    durationValue: 5,
    durationUnit: 'days',
    instructions: 'Take with lukewarm water',
  },
  {
    itemName: 'Cetirizine 10 mg',
    itemDetails: 'Once daily',
    intakeTiming: 'after_food',
    doseQuantity: '1 tab',
    scheduleMorning: false,
    scheduleAfternoon: false,
    scheduleNight: true,
    durationValue: 7,
    durationUnit: 'days',
    instructions: '',
  },
  {
    itemName: 'Vitamin C 500 mg',
    itemDetails: '',
    intakeTiming: 'with_food',
    doseQuantity: '1 tab',
    scheduleMorning: true,
    scheduleAfternoon: false,
    scheduleNight: false,
    durationValue: 30,
    durationUnit: 'days',
    instructions: '',
  },
];

// ─── Page ───

export function PrescriptionPrintWizardPage() {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {};

  // Load saved settings (org default).
  const { data: settingsRows = [] } = usePowerSyncQuery<PrescriptionPrintSettingsRow>(
    qPrescriptionPrintSettings(),
  );
  const savedRow = settingsRows[0] ?? null;

  // Load app_config so we can pre-fill branding for clinics that have not
  // overridden any field yet (PRD §9.1: logo-only clinics should still work).
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const appConfig = appConfigRows[0] ?? null;
  // PRD.284 — print font color + letterhead branding, edited here as a live
  // draft (persisted to the shared app_config.extra_json.print_branding blob on
  // Save). `brandingDraft` is null until the user edits a branding field.
  const savedBranding = useMemo(() => getPrintBranding(appConfig), [appConfig]);
  const [brandingDraft, setBrandingDraft] = useState<PrintBrandingConfig | null>(null);
  const effectiveBranding = brandingDraft ?? savedBranding;
  const effectiveFontColor = effectiveBranding.printFontColor;
  const effectiveLetterhead = useMemo(
    () => letterheadFromBranding(effectiveBranding, appConfig?.org_localname),
    [effectiveBranding, appConfig],
  );
  const patchBranding = useCallback(
    (patch: Partial<PrintBrandingConfig>) => {
      setBrandingDraft((prev) => ({ ...(prev ?? savedBranding), ...patch }));
      setSaveStatus('idle');
    },
    [savedBranding],
  );

  // Doctor identity for the printout is per-user — resolve it from the current
  // user's linked provider rather than asking them to type it. The wizard
  // saves only the org-level defaults; the doctor block renders from this
  // lookup at preview time and at actual print time (per printing user).
  const userId = user?.uid ?? '';
  const { data: linkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgId, userId),
  );
  const linkedProviderId = linkRows[0]?.provider_id ?? null;
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const linkedProvider = useMemo(
    () => (linkedProviderId ? (providerRows.find((p) => p.id === linkedProviderId) ?? null) : null),
    [linkedProviderId, providerRows],
  );
  const { name: doctorName, credentials: doctorCredentials } = useMemo(
    () => resolveDoctor(linkedProvider, user?.displayName ?? null),
    [linkedProvider, user?.displayName],
  );

  const [step, setStep] = useState<StepNum>(1);
  const [draft, setDraft] = useState<PrescriptionPrintSettingsUI | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Initialize draft when saved row or app_config branding arrives.
  useEffect(() => {
    if (draft) return;
    if (savedRow || appConfig) {
      setDraft(resolvePrintSettings(savedRow, appConfig));
    }
  }, [savedRow, appConfig, draft]);

  // Effective view-model used by the preview pane: even when the draft hasn't
  // been initialised yet (no saved row, app_config still loading), the preview
  // should fall back to defaults so the surface is never blank.
  const effective = useMemo<PrescriptionPrintSettingsUI>(() => {
    if (draft) return draft;
    return { id: '', ...PRESCRIPTION_PRINT_DEFAULTS };
  }, [draft]);

  const setDraftPatch = useCallback((patch: Partial<PrescriptionPrintSettingsUI>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setSaveStatus('idle');
  }, []);

  const handleSave = useCallback(async () => {
    if (!db || !orgId || !draft) return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      const cols = mapUIToPrescriptionPrintSettingsColumns(draft);
      const now = new Date().toISOString();
      if (savedRow) {
        await db.execute(
          `UPDATE prescription_print_settings
           SET template_id = ?, logo_source = ?, clinic_name = ?, clinic_address = ?,
               clinic_phone = ?, clinic_email = ?, clinic_website = ?, footer_text = ?,
               show_patient_demographics = ?, show_complaint = ?, show_vitals = ?,
               show_item_details = ?, show_instructions = ?, show_doctor_footer = ?,
               show_signature_block = ?, updated_at = ?, updated_by = ?
           WHERE id = ?`,
          [
            cols.template_id,
            cols.logo_source,
            cols.clinic_name,
            cols.clinic_address,
            cols.clinic_phone,
            cols.clinic_email,
            cols.clinic_website,
            cols.footer_text,
            cols.show_patient_demographics,
            cols.show_complaint,
            cols.show_vitals,
            cols.show_item_details,
            cols.show_instructions,
            cols.show_doctor_footer,
            cols.show_signature_block,
            now,
            user?.uid ?? null,
            savedRow.id,
          ],
        );
      } else {
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO prescription_print_settings
            (id, org_id, clinic_id, template_id, logo_source, clinic_name, clinic_address,
             clinic_phone, clinic_email, clinic_website, footer_text,
             show_patient_demographics, show_complaint, show_vitals, show_item_details,
             show_instructions, show_doctor_footer, show_signature_block,
             created_at, updated_at, updated_by)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            cols.template_id,
            cols.logo_source,
            cols.clinic_name,
            cols.clinic_address,
            cols.clinic_phone,
            cols.clinic_email,
            cols.clinic_website,
            cols.footer_text,
            cols.show_patient_demographics,
            cols.show_complaint,
            cols.show_vitals,
            cols.show_item_details,
            cols.show_instructions,
            cols.show_doctor_footer,
            cols.show_signature_block,
            now,
            now,
            user?.uid ?? null,
          ],
        );
      }

      // PRD.284 — persist the print branding (font color + letterhead fields)
      // into the shared app_config.extra_json.print_branding blob.
      if (brandingDraft != null) {
        const existing = await db.getAll<{ extra_json: string | null }>(
          `SELECT extra_json FROM app_config WHERE id = 'app' LIMIT 1`,
        );
        if (existing.length > 0) {
          const nextExtra = writePrintBranding(existing[0].extra_json ?? null, brandingDraft);
          await db.execute(
            `UPDATE app_config SET extra_json = ?, updated_at = ?, updated_by = ? WHERE id = 'app'`,
            [nextExtra, now, user?.uid ?? null],
          );
        }
      }
      setSaveStatus('saved');
    } catch (err) {
      console.error('[PrescriptionPrintWizard] save failed', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [db, orgId, user, draft, savedRow, brandingDraft]);

  // Print Preview renders the sample prescription into an isolated iframe (not a
  // hidden on-page mount), so the host page never leaks into the printout on
  // tablet/mobile.
  const handlePrintPreview = useCallback(() => {
    const html = renderToStaticMarkup(
      <PrescriptionPrintLayout
        settings={effective}
        doctor={{ name: doctorName, credentials: doctorCredentials }}
        patient={{
          name: 'John Doe',
          age: '34 yrs',
          sex: 'M',
          patientNo: 'P-00124',
          contact: '+91 98765 43210',
        }}
        date={new Date().toISOString()}
        items={SAMPLE_ITEMS}
        complaint="Headache and mild fever for 2 days"
        vitalsSummary="BP 120/80 · HR 78 · Temp 37.2 °C"
        clinical={SAMPLE_CLINICAL}
        vitals={SAMPLE_VITALS}
        doctorRegistrationNo="REG-12345"
        weightAndBp="74 Kg / 120/80 mmHg"
        accentColor={primary}
        fontColor={effectiveFontColor}
        letterhead={effectiveLetterhead}
      />,
    );
    printHtmlViaIframe(html, 'Prescription');
  }, [effective, doctorName, doctorCredentials, primary, effectiveFontColor, effectiveLetterhead]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="pp-no-print">
        <PageHeader
          title="Prescription Print"
          subtitle="Configure how prescription printouts look — choose a template, set branding, then save."
          collapsed={collapsed}
          onToggle={onToggleSidebar}
        />
        <div style={{ padding: '0 var(--sp-5) var(--sp-3)' }}>
          <StepIndicator currentStep={step} primary={primary} onStepClick={(s) => setStep(s)} />
        </div>
      </div>

      <div
        className="pp-no-print"
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(420px, 520px) 1fr',
          gap: 'var(--sp-5)',
          padding: '0 var(--sp-5) var(--sp-5)',
          alignItems: 'stretch',
        }}
      >
        {/* Left: step content */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-5)' }}>
            {step === 1 && (
              <TemplateStep
                templateId={effective.templateId}
                primary={primary}
                onSelect={(id) => setDraftPatch({ templateId: id })}
              />
            )}
            {step === 2 && (
              <BrandingStep
                settings={effective}
                onChange={setDraftPatch}
                primary={primary}
                doctorName={doctorName}
                doctorCredentials={doctorCredentials}
                doctorLinked={Boolean(linkedProvider)}
                branding={effectiveBranding}
                onBrandingChange={patchBranding}
                showLetterheadFields={effective.templateId === 'classic_letterhead'}
              />
            )}
            {step === 3 && (
              <SectionsStep settings={effective} onChange={setDraftPatch} primary={primary} />
            )}
            {step === 4 && (
              <SaveStep
                saving={saving}
                saveStatus={saveStatus}
                onSave={handleSave}
                primary={primary}
              />
            )}
          </div>

          {/* Step nav */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              padding: 'var(--sp-3) var(--sp-5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--bg-surface-2, #fafafa)',
            }}
          >
            <button
              type="button"
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as StepNum) : s))}
              disabled={step === 1}
              style={{
                height: 34,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: step === 1 ? 'var(--text-disabled)' : 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                cursor: step === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Back
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s + 1) as StepNum)}
                style={{
                  height: 34,
                  padding: '0 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: primary,
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: 'pointer',
                }}
              >
                Next
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handlePrintPreview}
                  style={{
                    height: 34,
                    padding: '0 14px',
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${primary}`,
                    background: 'transparent',
                    color: primary,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <NavIcon name="file-text" /> Print Preview
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    height: 34,
                    padding: '0 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: primary,
                    color: '#fff',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save Layout'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: live preview */}
        <PreviewPane
          settings={effective}
          doctorName={doctorName}
          doctorCredentials={doctorCredentials}
          accentColor={primary}
          fontColor={effectiveFontColor}
          letterhead={effectiveLetterhead}
        />
      </div>
    </div>
  );
}

// ─── Step indicator ───

function StepIndicator({
  currentStep,
  primary,
  onStepClick,
}: {
  currentStep: StepNum;
  primary: string;
  onStepClick: (step: StepNum) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STEPS.map((s, idx) => {
        const isActive = s.num === currentStep;
        const isDone = s.num < currentStep;
        return (
          <div key={s.num} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <button
              type="button"
              onClick={() => onStepClick(s.num)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isActive ? primary : isDone ? 'var(--text)' : 'var(--text-muted)',
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  background: isActive
                    ? primary
                    : isDone
                      ? 'var(--bg-surface-2, #f5f5f5)'
                      : 'transparent',
                  color: isActive ? '#fff' : isDone ? primary : 'var(--text-muted)',
                  border: isActive
                    ? `1px solid ${primary}`
                    : `1px solid ${isDone ? primary : 'var(--border)'}`,
                }}
              >
                {isDone ? '✓' : s.num}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: isActive ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                }}
              >
                {s.label}
              </span>
            </button>
            {idx < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: s.num < currentStep ? primary : 'var(--border)',
                  margin: '0 4px',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Template ───

function TemplateStep({
  templateId,
  primary,
  onSelect,
}: {
  templateId: PrescriptionPrintTemplateId;
  primary: string;
  onSelect: (id: PrescriptionPrintTemplateId) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <StepHeading
        title="Choose a system template"
        description="Pick a starting layout. You can change templates anytime — your branding and section choices will carry over."
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-3)',
        }}
      >
        {SYSTEM_TEMPLATES.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            selected={t.id === templateId}
            primary={primary}
            onSelect={() => onSelect(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  selected,
  primary,
  onSelect,
}: {
  template: SystemTemplate;
  selected: boolean;
  primary: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: `1.5px solid ${selected ? primary : 'var(--border)'}`,
        background: selected ? `${primary}10` : 'var(--bg-surface)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {template.label}
        </span>
        {selected && <span style={{ color: primary, fontSize: 14, fontWeight: 700 }}>✓</span>}
      </div>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}
      >
        {template.description}
      </span>
    </button>
  );
}

// ─── Step 2: Branding ───

function BrandingStep({
  settings,
  onChange,
  primary,
  doctorName,
  doctorCredentials,
  doctorLinked,
  branding,
  onBrandingChange,
  showLetterheadFields,
}: {
  settings: PrescriptionPrintSettingsUI;
  onChange: (patch: Partial<PrescriptionPrintSettingsUI>) => void;
  primary: string;
  doctorName: string;
  doctorCredentials: string;
  doctorLinked: boolean;
  branding: PrintBrandingConfig;
  onBrandingChange: (patch: Partial<PrintBrandingConfig>) => void;
  showLetterheadFields: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <StepHeading
        title="Branding"
        description="These appear in the printout's header and footer. Pick which configured logo to use — text-only headers also work."
      />
      <FieldGroup
        label="Print Font Color"
        hint="Applies to printed text + template chrome (used by Letterhead Classic)."
      >
        <PrintFontColorInput
          value={branding.printFontColor}
          onChange={(v) => onBrandingChange({ printFontColor: v })}
        />
      </FieldGroup>

      {showLetterheadFields && <LetterheadFields branding={branding} onChange={onBrandingChange} />}

      {templateSupports(settings.templateId, 'logoSize') && (
        <FieldGroup
          label="Logo Size"
          hint="Header logo height. Independent of Header Size — a large logo in a compact header is a valid choice."
        >
          <ChoiceRow
            options={LOGO_SIZES}
            value={settings.layout.logoSize}
            onChange={(v) => onChange({ layout: { ...settings.layout, logoSize: v } })}
            primary={primary}
          />
        </FieldGroup>
      )}

      {templateSupports(settings.templateId, 'headerSize') && (
        <FieldGroup
          label="Header Size"
          hint="Clinic name scale and header padding. Does not resize the logo."
        >
          <ChoiceRow
            options={HEADER_SIZES}
            value={settings.layout.headerSize}
            onChange={(v) => onChange({ layout: { ...settings.layout, headerSize: v } })}
            primary={primary}
          />
        </FieldGroup>
      )}

      <FieldGroup label="Logo">
        <LogoSourcePicker
          value={settings.logoSource}
          onChange={(v) => onChange({ logoSource: v })}
          primary={primary}
        />
      </FieldGroup>
      <FieldGroup
        label="Clinic Name (optional)"
        hint="Leave blank if your horizontal logo already shows it."
      >
        <TextInput value={settings.clinicName} onChange={(v) => onChange({ clinicName: v })} />
      </FieldGroup>
      <FieldGroup
        label="Doctor"
        hint="Pulled from your linked provider profile. Update it under Appointments → Providers."
      >
        <DoctorCard
          name={doctorName}
          credentials={doctorCredentials}
          linked={doctorLinked}
          primary={primary}
        />
      </FieldGroup>
      <FieldGroup label="Clinic Address">
        <TextArea
          value={settings.clinicAddress}
          rows={2}
          onChange={(v) => onChange({ clinicAddress: v })}
        />
      </FieldGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Clinic Phone">
          <TextInput value={settings.clinicPhone} onChange={(v) => onChange({ clinicPhone: v })} />
        </FieldGroup>
        <FieldGroup label="Clinic Email">
          <TextInput value={settings.clinicEmail} onChange={(v) => onChange({ clinicEmail: v })} />
        </FieldGroup>
      </div>
      <FieldGroup label="Clinic Website">
        <TextInput
          value={settings.clinicWebsite}
          placeholder="www.example.com"
          onChange={(v) => onChange({ clinicWebsite: v })}
        />
      </FieldGroup>
      <FieldGroup label="Footer Text">
        <TextArea
          value={settings.footerText}
          rows={2}
          placeholder="e.g. Disclaimer / clinic timings / regulatory note"
          onChange={(v) => onChange({ footerText: v })}
        />
      </FieldGroup>
    </div>
  );
}

// ─── Doctor card (read-only, resolved from user's linked provider) ───

function DoctorCard({
  name,
  credentials,
  linked,
  primary,
}: {
  name: string;
  credentials: string;
  linked: boolean;
  primary: string;
}) {
  if (!linked || !name) {
    return (
      <div
        style={{
          padding: 'var(--sp-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px dashed var(--border)',
          background: 'var(--bg-surface-2, #fafafa)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}
      >
        No provider is linked to your user account. Link your account to a provider under
        Appointments → Providers to have your name and specialty appear on the printout.
      </div>
    );
  }
  return (
    <div
      style={{
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${primary}40`,
        background: `${primary}08`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {name}
      </div>
      {credentials && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {credentials}
        </div>
      )}
    </div>
  );
}

// ─── Logo source picker ───

function LogoSourcePicker({
  value,
  onChange,
  primary,
}: {
  value: PrescriptionPrintLogoSource;
  onChange: (v: PrescriptionPrintLogoSource) => void;
  primary: string;
}) {
  const options: Array<{
    value: PrescriptionPrintLogoSource;
    label: string;
    hint: string;
    url: string;
  }> = [
    {
      value: 'standard',
      label: 'Square Logo',
      hint: orgLogoUrl ? 'Configured for this clinic' : 'Not configured for this build',
      url: orgLogoUrl,
    },
    {
      value: 'horizontal',
      label: 'Horizontal Logo',
      hint: orgLogoHorizontalUrl ? 'Configured for this clinic' : 'Not configured for this build',
      url: orgLogoHorizontalUrl,
    },
    {
      value: 'none',
      label: 'No Logo',
      hint: 'Text-only header',
      url: '',
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--sp-2)' }}>
      {options.map((opt) => {
        const selected = value === opt.value;
        const disabled = (opt.value === 'standard' || opt.value === 'horizontal') && !opt.url;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => !disabled && onChange(opt.value)}
            disabled={disabled}
            aria-pressed={selected}
            style={{
              textAlign: 'left',
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              border: `1.5px solid ${selected ? primary : 'var(--border)'}`,
              background: selected
                ? `${primary}10`
                : disabled
                  ? 'var(--bg-surface-2, #fafafa)'
                  : 'var(--bg-surface)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              minHeight: 96,
            }}
          >
            <div
              style={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                border: '1px dashed var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {opt.url ? (
                <img
                  src={opt.url}
                  alt=""
                  style={{
                    maxHeight: 40,
                    maxWidth: opt.value === 'horizontal' ? 140 : 40,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    fontStyle: 'italic',
                  }}
                >
                  {opt.value === 'none' ? 'No logo' : 'Not set'}
                </span>
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {opt.label}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {opt.hint}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * PRD.320 — representative synthetic content for the Dental Structured preview.
 *
 * Fed into the SAME `PrescriptionPrintLayout` production uses, so what the
 * clinic previews is what it prints. Purely illustrative: no patient data.
 */
const SAMPLE_CLINICAL: EncounterPrintClinical = {
  chiefComplaint: 'Pain in the upper right back tooth for one week, worse at night.',
  clinicalFindings: [
    { context: '16', text: 'Deep carious lesion involving the mesial and occlusal surfaces.' },
    { context: '', text: 'Generalized marginal gingivitis.' },
  ],
  investigations: [{ context: '16', text: 'IOPA — Periapical radiolucency at the mesial root.' }],
  diagnosis: [
    { context: '16', text: 'Irreversible pulpitis' },
    { context: '', text: 'Chronic generalized gingivitis' },
  ],
  treatmentAdvised: [
    { context: '16', text: 'Root Canal Treatment' },
    { context: '16', text: 'Porcelain Fused to Metal Crown' },
  ],
  treatmentDone: [
    { context: '16', text: 'Access opening — Patient tolerated the procedure well.' },
  ],
};

const SAMPLE_VITALS = {
  height: '168 cm',
  weight: '64 kg',
  bloodPressure: '120/80 mmHg',
  pulse: '78 bpm',
  spo2: '98 %',
};

// ─── Step 3: Sections ───

function SectionsStep({
  settings,
  onChange,
  primary,
}: {
  settings: PrescriptionPrintSettingsUI;
  onChange: (patch: Partial<PrescriptionPrintSettingsUI>) => void;
  primary: string;
}) {
  const clinicalNotesSupported = templateSupports(settings.templateId, 'clinicalNotes');

  // PRD.327 — Vitals and the Clinical Note master are locked ON for a template
  // that carries a clinical note. Reconciled here so the stored settings match
  // what the (disabled) checkboxes show: a control displayed as on while `false`
  // is persisted would print a Consultation Sheet missing the section it
  // promised. The Prescription printout suppresses both at RUNTIME regardless,
  // via `applyPrintPurpose` — nothing about that is stored.
  useEffect(() => {
    if (!clinicalNotesSupported) return;
    if (settings.showVitals && settings.sections.clinicalNotes) return;
    onChange({
      showVitals: true,
      sections: { ...settings.sections, clinicalNotes: true },
    });
  }, [clinicalNotesSupported, settings.showVitals, settings.sections, onChange]);
  const sectionLayoutSupported = templateSupports(settings.templateId, 'clinicalSectionLayout');

  const toggles: Array<{
    key: keyof PrescriptionPrintSettingsUI;
    label: string;
    description: string;
  }> = [
    {
      key: 'showPatientDemographics',
      label: 'Patient demographics',
      description: 'Patient name, age, sex',
    },
    ...(clinicalNotesSupported
      ? // Complaint is rendered inside the Clinical Note group below, under the
        // master toggle that actually governs it.
        []
      : [
          {
            key: 'showComplaint' as const,
            label: 'Complaint / diagnosis summary',
            description: 'Single-line summary block',
          },
        ]),
    ...(clinicalNotesSupported
      ? // PRD.327 — on a template that carries a clinical note, Vitals belongs
        // to the CONSULTATION SHEET only. The Prescription printout always
        // suppresses it at runtime, so the toggle is shown on and locked rather
        // than offering a choice that one of the two printouts ignores.
        []
      : [
          {
            key: 'showVitals' as const,
            label: 'Vitals summary',
            description: 'Compact vitals strip from the visit',
          },
        ]),
    {
      key: 'showItemDetails',
      label: 'Item details column',
      description: 'Brand, strength, formulation per item',
    },
    {
      key: 'showInstructions',
      label: 'Instructions column',
      description: 'Per-item instructions text',
    },
    {
      key: 'showDoctorFooter',
      label: 'Doctor footer',
      description: 'Doctor name + credentials at bottom',
    },
    {
      key: 'showSignatureBlock',
      label: 'Signature block',
      description: 'Signature line for the doctor',
    },
  ];

  // PRD.320 — clinical sections, shown only for the template that renders
  // them. Stored in `extra_json`, so a clinic that switches away keeps its
  // choices; the settings simply stop applying.
  // The five LIST-backed sections. `clinicalNotes` is the master toggle and
  // Complaint is free text, so neither belongs in this list.
  type ClinicalSectionKey = keyof PrescriptionPrintClinicalLayoutSettings;
  const clinicalToggles: Array<{
    key: ClinicalSectionKey;
    label: string;
    description: string;
  }> = [
    {
      key: 'clinicalFindings',
      label: 'Clinical Findings',
      description: "This visit's documented findings",
    },
    {
      key: 'investigations',
      label: 'Investigations',
      description: 'Radiographs and their findings',
    },
    { key: 'diagnosis', label: 'Diagnosis', description: 'Encounter diagnoses' },
    {
      key: 'treatmentAdvised',
      label: 'Treatment Advised',
      description: 'Planned / in-progress dental treatment',
    },
    {
      key: 'treatmentDone',
      label: 'Treatment Done',
      description: 'Treatment performed at this visit',
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <StepHeading
        title="Print sections"
        description="Toggle which blocks appear on the printout. Hidden sections are skipped in the generated layout."
      />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {toggles.map((t) => (
          <ToggleRow
            key={t.key}
            label={t.label}
            description={t.description}
            checked={Boolean(settings[t.key])}
            onChange={(v) => onChange({ [t.key]: v } as Partial<PrescriptionPrintSettingsUI>)}
          />
        ))}
      </div>

      {templateSupports(settings.templateId, 'vitalsLayout') && settings.showVitals && (
        <FieldGroup
          label="Vitals Layout"
          hint="How recorded vitals are presented. The section is omitted entirely when the visit has no vitals, whichever layout is chosen."
        >
          <ChoiceRow
            options={VITALS_LAYOUTS}
            value={settings.layout.vitalsLayout}
            onChange={(v) => onChange({ layout: { ...settings.layout, vitalsLayout: v } })}
            primary={primary}
          />
        </FieldGroup>
      )}

      {clinicalNotesSupported && (
        <>
          <StepHeading
            title="Consultation Sheet"
            description="Vitals, the Chief Complaint and the Clinical Note print on the Consultation Sheet only. The Prescription printout always excludes them, so it is purely a prescription — which is why Vitals and the Clinical Note are always on here."
          />
          <ToggleRow
            label="Vitals summary"
            description="Consultation Sheet only. Never printed on the Prescription."
            checked
            disabled
            onChange={() => {}}
          />
          <ToggleRow
            label="Clinical Note block"
            description="Consultation Sheet only. Never printed on the Prescription. Each subsection below is still yours to choose."
            checked
            disabled
            onChange={() => {}}
          />
          {/* Subsections remain fully editable: the master is locked on for a
              capable template, but WHICH sections appear on the Consultation
              Sheet is still the clinic's choice. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingLeft: 'var(--sp-4)',
              borderLeft: '2px solid var(--border)',
            }}
          >
            {/* Complaint is free text, so it has a toggle but no list layout —
                its own line breaks are preserved verbatim. It keeps its
                dedicated `show_complaint` column. */}
            <ToggleRow
              label="Chief Complaint"
              description="Free text, printed as entered. Consultation Sheet only — never on the Prescription."
              checked={settings.showComplaint}
              onChange={(v) => onChange({ showComplaint: v })}
            />
            {clinicalToggles.map((t) => (
              <div key={t.key}>
                <ToggleRow
                  label={t.label}
                  description={t.description}
                  checked={settings.sections[t.key]}
                  onChange={(v) => onChange({ sections: { ...settings.sections, [t.key]: v } })}
                />
                {settings.sections[t.key] && sectionLayoutSupported && (
                  <div style={{ padding: '0 0 var(--sp-3) var(--sp-4)' }}>
                    <ChoiceRow
                      options={MULTI_VALUE_LAYOUTS}
                      value={settings.clinicalSectionLayout[t.key]}
                      onChange={(v) =>
                        onChange({
                          clinicalSectionLayout: {
                            ...settings.clinicalSectionLayout,
                            [t.key]: v,
                          },
                        })
                      }
                      primary={primary}
                      size="sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {templateSupports(settings.templateId, 'typography') && (
        <>
          <StepHeading
            title="Typography"
            description="Semantic size presets, not raw font sizes. Sections left on Inherit follow the overall setting."
          />
          <FieldGroup label="Overall">
            <ChoiceRow
              options={FONT_SCALES}
              value={settings.typography.overall}
              onChange={(v) => onChange({ typography: { ...settings.typography, overall: v } })}
              primary={primary}
            />
          </FieldGroup>
          {TYPOGRAPHY_SECTIONS.map((sec) => (
            <FieldGroup key={sec.key} label={sec.label}>
              <ChoiceRow
                options={FONT_SCALE_CHOICES}
                value={settings.typography[sec.key]}
                onChange={(v) => onChange({ typography: { ...settings.typography, [sec.key]: v } })}
                primary={primary}
                size="sm"
              />
            </FieldGroup>
          ))}
        </>
      )}
    </div>
  );
}

/** Per-section typography rows. `overall` is rendered separately, above these. */
const TYPOGRAPHY_SECTIONS: Array<{
  key: Exclude<keyof PrescriptionPrintTypographySettings, 'overall'>;
  label: string;
}> = [
  { key: 'header', label: 'Header' },
  { key: 'patientDetails', label: 'Patient Details' },
  { key: 'vitals', label: 'Vitals' },
  { key: 'clinicalNote', label: 'Clinical Note' },
  { key: 'prescription', label: 'Prescription' },
  { key: 'footer', label: 'Footer' },
];

/**
 * A small segmented control over a fixed preset list.
 *
 * Generic over the option union so each caller keeps its own literal type —
 * `onChange` hands back `'small' | 'medium' | 'large'`, never a bare string,
 * which is what stops an invalid value reaching storage.
 */
function ChoiceRow<T extends string>({
  options,
  value,
  onChange,
  primary,
  size = 'md',
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  primary: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: size === 'sm' ? '3px 10px' : '6px 14px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${active ? primary : 'var(--border)'}`,
              background: active ? primary : 'var(--bg-surface)',
              color: active ? '#fff' : 'var(--text)',
              fontSize: size === 'sm' ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              fontWeight: active ? 'var(--font-weight-medium)' : undefined,
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ─── Step 4: Save ───

function SaveStep({
  saving,
  saveStatus,
  onSave,
  primary,
}: {
  saving: boolean;
  saveStatus: 'idle' | 'saved' | 'error';
  onSave: () => void;
  primary: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <StepHeading
        title="Preview & save"
        description="Review the live preview on the right. Save to make this the clinic's default prescription print layout."
      />
      <div
        style={{
          padding: 'var(--sp-3)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface-2, #fafafa)',
          border: '1px solid var(--border)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}
      >
        Saving stores this configuration as the org default. Per-clinic overrides can be added later
        without a schema change. The actual print action lives in the patient prescription workflow
        and uses this saved layout.
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        style={{
          alignSelf: 'flex-start',
          height: 36,
          padding: '0 16px',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: primary,
          color: '#fff',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? 'Saving…' : 'Save as Default Layout'}
      </button>
      {saveStatus === 'saved' && (
        <div
          style={{ color: 'var(--color-success-text, #15803d)', fontSize: 'var(--font-size-sm)' }}
        >
          Layout saved.
        </div>
      )}
      {saveStatus === 'error' && (
        <div style={{ color: '#dc2626', fontSize: 'var(--font-size-sm)' }}>
          Save failed. Check the console for details.
        </div>
      )}
    </div>
  );
}

// ─── Preview pane ───

// A4 dimensions at 96dpi (CSS pixels): 210mm × 297mm.
const A4_WIDTH_PX = (210 * 96) / 25.4; // ≈ 793.7
const A4_HEIGHT_PX = (297 * 96) / 25.4; // ≈ 1122.5

function PreviewPane({
  settings,
  doctorName,
  doctorCredentials,
  accentColor,
  fontColor,
  letterhead,
}: {
  settings: PrescriptionPrintSettingsUI;
  doctorName: string;
  doctorCredentials: string;
  accentColor: string;
  fontColor: string;
  letterhead: PrintLetterheadInfo;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Track the frame's content width and scale the A4 page down to fit.
  // Capped at 1 — paper documents look wrong when scaled above 100%.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / A4_WIDTH_PX));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f5f5f5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: 'var(--sp-3)',
          fontWeight: 'var(--font-weight-semi)',
        }}
      >
        Live Preview
      </div>

      {/* Scaling frame: full width of the column; scaled-down A4 page lives
          inside. Height is the scaled A4 height so the layout reserves the
          right amount of space. */}
      <div
        ref={frameRef}
        style={{
          width: '100%',
          height: A4_HEIGHT_PX * scale,
          position: 'relative',
          alignSelf: 'center',
          maxWidth: A4_WIDTH_PX,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: A4_WIDTH_PX,
            transformOrigin: 'top left',
            transform: `scale(${scale})`,
          }}
        >
          <PrescriptionPrintLayout
            settings={settings}
            doctor={{ name: doctorName, credentials: doctorCredentials }}
            patient={{
              name: 'John Doe',
              age: '34 yrs',
              sex: 'M',
              patientNo: 'P-00124',
              contact: '+91 98765 43210',
            }}
            date={new Date().toISOString()}
            items={SAMPLE_ITEMS}
            complaint="Headache and mild fever for 2 days"
            vitalsSummary="BP 120/80 · HR 78 · Temp 37.2 °C"
            clinical={SAMPLE_CLINICAL}
            vitals={SAMPLE_VITALS}
            doctorRegistrationNo="REG-12345"
            weightAndBp="74 Kg / 120/80 mmHg"
            accentColor={accentColor}
            fontColor={fontColor}
            letterhead={letterhead}
            previewMode
          />
        </div>
      </div>
    </div>
  );
}

// ─── Atoms ───

function StepHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {title}
      </h2>
      <p
        style={{
          margin: '4px 0 0',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
        }}
      >
        {description}
      </p>
    </div>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-placeholder, var(--text-muted))',
          }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  height: 34,
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

// PRD.284 — letterhead free-text fields, shown when the Letterhead Classic
// template is selected. Left header column: doctor name + qualifications /
// specialty / designation. Right header column: registration / panel / address.
function LetterheadFields({
  branding,
  onChange,
}: {
  branding: PrintBrandingConfig;
  onChange: (patch: Partial<PrintBrandingConfig>) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #f8fafc)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semi)' }}>
        Letterhead header
      </div>
      <FieldGroup
        label="Doctor name (left header)"
        hint="The large name in the left column. Leave blank to use your linked provider."
      >
        <TextInput
          value={branding.letterheadDoctorName}
          placeholder="e.g. Dr. M. Shanmugam"
          onChange={(v) => onChange({ letterheadDoctorName: v })}
        />
      </FieldGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Qualifications (left)">
          <TextInput
            value={branding.doctorQualifications}
            placeholder="e.g. MBBS, MD"
            onChange={(v) => onChange({ doctorQualifications: v })}
          />
        </FieldGroup>
        <FieldGroup label="Specialty (left)">
          <TextInput
            value={branding.doctorSpecialty}
            placeholder="e.g. Consultant Chest Diseases"
            onChange={(v) => onChange({ doctorSpecialty: v })}
          />
        </FieldGroup>
      </div>
      <FieldGroup label="Designation (left)">
        <TextInput
          value={branding.doctorDesignation}
          placeholder="e.g. Joint Director of Medical Services (Retd)"
          onChange={(v) => onChange({ doctorDesignation: v })}
        />
      </FieldGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Registration no. (right)">
          <TextInput
            value={branding.registrationNumber}
            placeholder="e.g. 22930"
            onChange={(v) => onChange({ registrationNumber: v })}
          />
        </FieldGroup>
        <FieldGroup label="Additional registrations (right)">
          <TextInput
            value={branding.additionalRegistrations}
            placeholder="e.g. Medical Examiner LIC, PLI & RPLI"
            onChange={(v) => onChange({ additionalRegistrations: v })}
          />
        </FieldGroup>
      </div>
      <FieldGroup label="Insurance / Panel (right)">
        <TextInput
          value={branding.insurancePanel}
          placeholder="e.g. Authorized Medical Attendant, SBI"
          onChange={(v) => onChange({ insurancePanel: v })}
        />
      </FieldGroup>
      <FieldGroup label="Additional footer lines" hint="Shown centered in the footer.">
        <TextArea
          value={branding.footerExtraText}
          rows={2}
          onChange={(v) => onChange({ footerExtraText: v })}
        />
      </FieldGroup>
    </div>
  );
}

// PRD.284 — color swatch + hex input for the print font color.
function PrintFontColorInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input
        type="color"
        aria-label="Print font color"
        value={normalizePrintFontColor(value)}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 44,
          height: 34,
          padding: 0,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-surface)',
          cursor: 'pointer',
        }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        style={{ ...inputStyle, maxWidth: 140 }}
      />
    </div>
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        ...inputStyle,
        height: 'auto',
        padding: '8px 10px',
        resize: 'vertical',
        fontFamily: 'inherit',
        lineHeight: 'var(--line-height-body)',
      }}
    />
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  /** Shown but not changeable — the value is dictated by the print purpose. */
  disabled?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) 0',
        borderBottom: '1px solid var(--border)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.75 : 1,
      }}
    >
      <div>
        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>
          {label}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {description}
        </div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: 18,
          height: 18,
          marginTop: 2,
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
    </label>
  );
}
